import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import PageBanner from "../components/PageBanner";

const servicesData = [
  {
    slug: "fdm",
    title: "Fused Deposition Modelling (FDM)",
    subtitle: "The Most Cost-Effective 3D Printing Technology",
    description: "Fused Deposition Modelling, popularly known as FDM, is one of the most sought-out and cost-effective ways of producing functional prototypes for strength and display. At 3D Printzkart, our print farm of 20+ FDM 3D printers in Bangalore delivers high-quality parts for prototyping, batch production, and end-use applications.",
    advantages: [
      "Most affordable 3D printing technology",
      "Wide range of engineering materials",
      "Large build volumes available"
    ]
  },
  {
    slug: "sla",
    title: "Stereolithography (SLA)",
    subtitle: "Ultra-High Precision Resin 3D Printing",
    description: "Stereolithography (SLA) is the gold standard for high-precision 3D printing, delivering parts with exceptionally smooth surface finish and intricate detail. At 3D Printzkart Bangalore, our SLA printers produce parts with micron-level accuracy — perfect for jewelry patterns, dental models, medical devices, and highly detailed prototypes.",
    advantages: [
      "Highest precision and detail among all 3D printing methods",
      "Smooth surface finish — near injection mold quality",
      "Ideal for small, intricate parts"
    ]
  },
  {
    slug: "sls",
    title: "Selective Laser Sintering (SLS / MJF)",
    subtitle: "Industrial-Grade Production Parts",
    description: "Selective Laser Sintering (SLS) and Multi Jet Fusion (MJF) are the most durable and sustainable additive manufacturing technologies for production-ready plastic parts. At 3D Printzkart Bangalore, we deliver SLS parts in Nylon (PA11, PA12) and Glass-Filled Nylon for industrial applications.",
    advantages: [
      "No support structures needed — ultimate design freedom",
      "Production-grade mechanical properties",
      "Both polymer and metal options"
    ]
  },
  {
    slug: "product-development",
    title: "Product Development",
    subtitle: "From Concept to Market-Ready Product",
    description: "At 3D Printzkart, we believe in the power of product design. Our product development service in Bangalore combines creative design thinking with DFAM (Design for Additive Manufacturing) principles to take your idea from napkin sketch to production-ready product. We serve startups, enterprises, and research institutions across India.",
    advantages: [
      "End-to-end product development under one roof",
      "DFAM-optimized designs for additive manufacturing",
      "Rapid iteration — prototype in 48 hours"
    ]
  },
  {
    slug: "post-processing",
    title: "Post Processing And Integration",
    subtitle: "From Print to Production-Ready",
    description: "Additive manufacturing produces complex parts quickly, but post-processing transforms them into functional, aesthetic, and production-ready components. At 3D Printzkart, we offer a range of finishing processes to improve surface quality, strength, aesthetics, and performance of printed parts.",
    advantages: [
      "End-to-end post-processing under one roof",
      "Improved surface quality and aesthetics",
      "Production-ready parts with tight tolerances"
    ]
  }
];

const ServicesPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <>
      <PageBanner
        title="Our 3D Printing Services"
        subtitle="Comprehensive Additive Manufacturing Solutions in Bangalore"
        image="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=1920&h=600&fit=crop"
        breadcrumbs={[{ label: "Services" }]}
      />

      <section className="inner-section" ref={ref}>
        <div className="inner-container">
          <motion.div
            className="services-page-intro"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">What We Offer</span>
            <h2 className="section-title">3D Printing Services in Bangalore</h2>
            <div className="section-line" />
            <p style={{ maxWidth: '900px', margin: '20px auto 48px', fontSize: '16px', lineHeight: '1.8', color: '#555', textAlign: 'center' }}>
              3D Printzkart offers a complete range of additive manufacturing services — from cost-effective FDM prototyping to high-precision SLA resin printing, industrial-grade SLS production parts, and end-to-end product development. All under one roof in Bangalore.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="services-page-grid">
            {servicesData.map((service, i) => (
              <motion.div
                key={service.slug}
                className="service-page-card"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.15 }}
              >
                <h3 className="spc-title">{service.title}</h3>
                <h4 className="spc-subtitle">{service.subtitle}</h4>
                <p className="spc-desc">{service.description}</p>
                <ul className="spc-advantages">
                  {service.advantages.map((adv, idx) => (
                    <li key={idx}>
                      <CheckCircle2 size={18} />
                      <span>{adv}</span>
                    </li>
                  ))}
                </ul>
                <Link to={`/services/${service.slug}`} className="btn-primary">
                  Learn More <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>
        <div className="cta-inner">
          <h2 className="cta-title">Not Sure Which Technology Is Right For You?</h2>
          <p className="cta-desc">
            Our engineers will analyze your design and recommend the best 3D printing technology, material, and finish for your project. Free consultation included.
          </p>
          <Link to="/contact" className="btn-primary">
            Get Free Consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
